// src/features/chat/templates/approvalCardTemplates.js
/*
 * Design-time template library for approval commit cards.
 *
 * Commit language is rendered, not generated: every string on a card comes
 * from this module, parameterized only by server-asserted structured fields
 * on the approval view doc. No model-authored text reaches a card.
 *
 * English v1; structured so locale routing can be added without touching
 * components (all copy lives in this directory).
 */

export const BOUNDARY_APPROVAL_OPERATION_KINDS = new Set([
  'record.access.grant',
  'record.access.role_change',
  'record.access.revoke',
  'record.visibility.set',
]);

const ROLE_LABELS = {
  view: 'Can view',
  edit: 'Can edit',
  admin: 'Admin',
};

const VISIBILITY_LABELS = {
  private: 'Private',
  friends: 'Friends',
  public: 'Anyone on Affin',
};

function safeLabel(value, fallback) {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

export function roleLabel(role) {
  if (!role) return 'No direct access';
  return ROLE_LABELS[role] || role;
}

export function visibilityLabel(visibility) {
  if (!visibility) return 'Unknown';
  return VISIBILITY_LABELS[visibility] || visibility;
}

export function principalLabel(approval) {
  const principal = approval?.principal || {};
  if (principal.type === 'group') {
    return safeLabel(principal.label, 'Selected group');
  }
  return safeLabel(principal.label, 'Selected user');
}

export function isBoundaryApproval(approval) {
  return BOUNDARY_APPROVAL_OPERATION_KINDS.has(approval?.operationKind);
}

function isDeleteApproval(approval) {
  return (
    approval?.operationKind === 'record.delete' ||
    approval?.kind === 'record_delete' ||
    approval?.disclosure?.destructive === true
  );
}

/**
 * Risk tier of the commit (renders the trust boundary, not CRUD kind).
 * - 'destructive': something is destroyed → red edge.
 * - 'boundary': anything reaches other people → amber edge.
 * - 'plain': owner-only world commit.
 * @param {Object} approval
 * @returns {'destructive'|'boundary'|'plain'}
 */
export function approvalTier(approval) {
  if (isDeleteApproval(approval)) return 'destructive';
  const delta = approval?.audienceDelta || {};
  if (
    delta.expands ||
    delta.sourceExpands ||
    approval?.sideEffects?.notify?.enabled
  ) {
    return 'boundary';
  }
  return 'plain';
}

/**
 * Small chip naming the boundary crossing, amber tier only.
 * @param {Object} approval
 * @returns {string|null}
 */
export function boundaryChip(approval) {
  return approvalTier(approval) === 'boundary' ? 'New audience' : null;
}

/**
 * Plain noun-phrase title naming the change and target.
 * @param {Object} approval
 * @returns {string}
 */
export function approvalTitle(approval) {
  switch (approval?.operationKind) {
    case 'record.access.grant':
      return `Share this Record with ${principalLabel(approval)}`;
    case 'record.access.role_change':
      return `Change ${principalLabel(approval)}’s access`;
    case 'record.access.revoke':
      return `Remove ${principalLabel(approval)}’s access`;
    case 'record.visibility.set': {
      const after = approval?.accessChange?.after?.visibility;
      if (after === 'private') return 'Make this Record private';
      if (after) return `Make this Record visible to ${visibilityLabel(after).toLowerCase()}`;
      return 'Change who can see this Record';
    }
    default:
      break;
  }
  if (isDeleteApproval(approval)) return 'Delete this Record';
  return 'Update this Record';
}

/**
 * Outcome-labeled buttons. Buttons name world outcomes, never process.
 * @param {Object} approval
 * @returns {{confirm: string, decline: string}}
 */
export function approvalButtons(approval) {
  switch (approval?.operationKind) {
    case 'record.access.grant':
      return { confirm: 'Share', decline: 'Don’t share' };
    case 'record.access.role_change':
      return { confirm: 'Change access', decline: 'Keep current access' };
    case 'record.access.revoke':
      return { confirm: 'Remove access', decline: 'Keep access' };
    case 'record.visibility.set': {
      const before = approval?.accessChange?.before?.visibility;
      const after = approval?.accessChange?.after?.visibility;
      const confirm = after === 'private'
        ? 'Make private'
        : after
          ? `Make visible to ${visibilityLabel(after).toLowerCase()}`
          : 'Change visibility';
      // Decline names the world state that stays, not a process.
      const decline = before === 'private'
        ? 'Keep private'
        : before === 'friends'
          ? 'Keep to friends'
          : before === 'public'
            ? 'Keep public'
            : 'Keep as is';
      return { confirm, decline };
    }
    default:
      break;
  }
  if (isDeleteApproval(approval)) {
    return { confirm: 'Delete Record', decline: 'Keep it' };
  }
  return { confirm: 'Apply update', decline: 'Not now' };
}

function diffPreviewText(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || '(empty)';
}

function diffRowLabel(entry) {
  if (entry?.label) return entry.label;
  return String(entry?.path || '').replace(/^content\./, '') || 'Content';
}

/**
 * One picturable line for a server-asserted semantic diff row — the words
 * are the UI: the card must say WHAT changes, not that something changes.
 * @param {Object} entry - semanticDiff row (server-asserted, commit-pinned).
 * @returns {string}
 */
export function diffRowSummary(entry) {
  const label = diffRowLabel(entry);
  const changeKind = entry?.changeKind || 'replace';
  if (changeKind === 'add' || changeKind === 'set') {
    return `${label}: ${diffPreviewText(entry.afterPreview)}`;
  }
  if (changeKind === 'remove' || changeKind === 'delete' || changeKind === 'soft_delete') {
    return `${label} removed`;
  }
  if (changeKind === 'already_present') {
    return `${label}: ${diffPreviewText(entry.afterPreview || entry.beforePreview)}`;
  }
  return `${label}: ${diffPreviewText(entry.beforePreview)} → ${diffPreviewText(entry.afterPreview)}`;
}

/**
 * One-line summary under the title — design-time copy from structured
 * fields only. Boundary and destructive tiers carry their consequence in
 * the unfolded floor instead, so their summary is null (the card anatomy is
 * "one-line summary or up to 3 short facts"). Plain updates lead with the
 * actual change from the commit-pinned semantic diff.
 * @param {Object} approval
 * @returns {string|null}
 */
export function approvalSummary(approval) {
  const tier = approvalTier(approval);
  if (tier !== 'plain') return null;
  if (isBoundaryApproval(approval)) {
    // Restricts-only boundary operations (plain tier): name the narrowing.
    const delta = approval?.audienceDelta || {};
    if (delta.restricts || delta.sourceRestricts) {
      return `Narrows who can ${approval?.operationKind === 'record.visibility.set' ? 'see' : 'reach'} this Record`;
    }
    return 'Adjusts who can reach this Record';
  }
  const rows = Array.isArray(approval?.semanticDiff) ? approval.semanticDiff : [];
  if (rows.length === 0) return 'Changes this Record’s content';
  const lead = diffRowSummary(rows[0]);
  return rows.length > 1 ? `${lead} · ${rows.length - 1} more` : lead;
}

function audienceCountSuffix(approval) {
  const delta = approval?.audienceDelta || {};
  if (Number.isInteger(delta.affectedCount) && delta.affectedCount > 0) {
    return ` · ${delta.affectedCount} ${delta.affectedCount === 1 ? 'person' : 'people'}`;
  }
  if (delta.dynamicAudience?.type === 'owner_friends') {
    return ' · follows your friends list';
  }
  if (delta.dynamicAudience?.type === 'group_membership') {
    return ' · follows group membership';
  }
  return '';
}

function affectedNames(approval) {
  const principals = approval?.audienceDelta?.affectedPrincipals;
  if (!Array.isArray(principals)) return null;
  const labels = principals
    .map((principal) => safeLabel(principal?.label, ''))
    .filter(Boolean);
  if (!labels.length) return null;
  const capped = labels.slice(0, 3).join(', ');
  return labels.length > 3 ? `${capped}, and others` : capped;
}

/**
 * Disclosure floor: facts that stay unfolded on the card, by tier.
 * - boundary: who newly sees or receives what, in names and numbers.
 * - destructive: blast radius. (A recoverability fact is deliberately absent:
 *   no user-facing recovery for soft-deleted Records exists yet, and a
 *   "recoverable for N days" promise without that capability would be
 *   hallucinated reassurance at the moment of commitment. Reintroduce the
 *   fact together with a server-asserted field once recovery ships.)
 * - plain: no floor (diff folds behind Details).
 *
 * Facts may carry a `strong` substring — the load-bearing token (a name, a
 * count, an audience) rendered in weight, per "hierarchy carried by weight".
 * @param {Object} approval
 * @returns {Array<{id: string, text: string, strong?: string}>}
 */
export function approvalFloor(approval) {
  const tier = approvalTier(approval);
  const facts = [];

  if (tier === 'destructive') {
    facts.push({ id: 'blast', text: 'Deletes this Record from your world' });
    return facts;
  }

  if (tier !== 'boundary') return facts;

  const delta = approval?.audienceDelta || {};
  const before = approval?.accessChange?.before || {};
  const after = approval?.accessChange?.after || {};

  if (approval?.operationKind === 'record.visibility.set') {
    const audience = `${visibilityLabel(after.visibility)}${audienceCountSuffix(approval)}`;
    facts.push({
      id: 'audience',
      text: `${visibilityLabel(before.visibility)} → ${audience}`,
      strong: audience,
    });
  } else if (delta.expands || delta.sourceExpands) {
    const names = affectedNames(approval) || principalLabel(approval);
    facts.push({
      id: 'audience',
      text: `${names} — ${roleLabel(after.role || delta.effectiveAfterRole).toLowerCase()}`,
      strong: names,
    });
  }

  if (delta.publicAccessDisclosure?.type === 'authenticated_public_view_read_enabled') {
    facts.push({
      id: 'public',
      text: 'Anyone signed in to Affin could view this',
      strong: 'Anyone signed in to Affin',
    });
  }

  const notify = approval?.sideEffects?.notify;
  if (notify?.enabled) {
    const recipient = principalLabel(approval);
    facts.push({
      id: 'notify',
      text: approval?.principal?.type === 'group'
        ? `${recipient} gets a group message`
        : `${recipient} gets a notification`,
      strong: recipient,
    });
  }

  return facts;
}

/**
 * Record links carried by the approval (source + linked targets).
 * @param {Object} approval
 * @returns {Array<{id: string, label: string}>}
 */
export function approvalRecordLinks(approval) {
  const links = [];
  const sourceRecordId = approval?.targetRecordRef?.recordId;
  if (sourceRecordId) {
    links.push({ id: sourceRecordId, label: 'Open Record' });
  }
  const targetRefs = Array.isArray(approval?.targetRecordRefs)
    ? approval.targetRecordRefs
    : [];
  targetRefs.forEach((ref) => {
    if (ref?.targetType !== 'record' || !ref.id) return;
    links.push({
      id: ref.id,
      label: ref.label ? `Linked Record: ${ref.label}` : 'Open Linked Record',
    });
  });
  return links;
}

/**
 * Folded Details content. Semantic diff for owner edits; short picturable
 * bullets for boundary mechanics. Null when there is nothing to fold.
 * @param {Object} approval
 * @returns {{kind: 'diff', rows: Array}|{kind: 'bullets', bullets: Array<{id: string, text: string}>}|null}
 */
export function approvalDetails(approval) {
  const diffRows = Array.isArray(approval?.semanticDiff) ? approval.semanticDiff : [];
  if (diffRows.length > 0) {
    return { kind: 'diff', rows: diffRows };
  }
  if (!isBoundaryApproval(approval)) return null;

  const bullets = [];
  const delta = approval?.audienceDelta || {};

  if (delta.restricts || delta.sourceRestricts) {
    const names = affectedNames(approval) || principalLabel(approval);
    bullets.push({
      id: 'restricts',
      text: `${names} loses this access — ${roleLabel(delta.effectiveAfterRole).toLowerCase()} may remain another way`,
    });
  }
  if (delta.mayRetainAccessViaOtherSources) {
    bullets.push({
      id: 'retained',
      text: 'Some people may still have access another way — through visibility, direct shares, or groups',
    });
  }
  if (delta.dynamicAudience?.type === 'owner_friends') {
    bullets.push({
      id: 'dynamic',
      text: 'Access follows your friends list as it changes, not a fixed set of people',
    });
  }
  if (delta.dynamicAudience?.type === 'group_membership') {
    bullets.push({
      id: 'dynamic',
      text: 'Access follows the group’s membership as it changes, not a fixed set of people',
    });
  }
  if (delta.publicAccessDisclosure?.type === 'authenticated_public_view_read_removed') {
    bullets.push({
      id: 'public-removed',
      text: 'Anyone-on-Affin viewing is removed where it depended on this',
    });
  }

  const reversibility = approval?.reversibility;
  if (reversibility?.accessStateChangeableLater === false) {
    bullets.push({
      id: 'reversibility',
      text: 'This can’t be safely reversed from here',
    });
  } else if (reversibility?.emittedSideEffectsNotUndone) {
    bullets.push({
      id: 'reversibility',
      text: 'You can change this later — messages already sent stay sent',
    });
  } else if (reversibility) {
    bullets.push({
      id: 'reversibility',
      text: 'You can change this later — what people already saw stays seen',
    });
  }

  const notify = approval?.sideEffects?.notify;
  if (notify?.enabled && approval?.sideEffects?.emitsOnlyAfterApprovedAccessExpansion) {
    bullets.push({
      id: 'notify-timing',
      text: 'The message goes out only after the change is applied',
    });
  }
  if (notify?.enabled && notify?.hasCustomMessage) {
    bullets.push({
      id: 'notify-custom',
      text: 'Includes a custom message',
    });
  }

  return bullets.length ? { kind: 'bullets', bullets } : null;
}
